import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/ImageCropEditor.css';

// `listRatio` is the aspect ratio (width / height) the image is actually displayed at inside the
// media list tiles (see .thumbnail / .thumbnail_poster in App.css) - it doesn't always match the
// crop viewport's own ratio, so part of the crop can end up hidden there.
const ASPECTS = [
  { key: 'vertical', label: 'Poster', ratio: 2 / 3, viewport: { width: 220, height: 330 }, listRatio: 100 / 150 },
  { key: 'horizontal', label: 'Thumbnail', ratio: 16 / 9, viewport: { width: 360, height: 202.5 }, listRatio: 230 / 100 },
];

const MIN_ZOOM_LOCKED = 1;
const MIN_ZOOM_FREE = 0.5;
const MAX_ZOOM = 3;
const OUT_OF_FRAME_SLACK = 0.5; // fraction of the frame that can be left empty when enabled
const OUT_OF_FRAME_BLUR = 80; // px

// Works out which edges of the crop viewport get clipped off when shown at the list's aspect
// ratio, so we can dim them to preview what will actually be visible there.
const getListMask = (aspect) => {
  if (!aspect.listRatio || aspect.listRatio === aspect.ratio) return null;
  if (aspect.listRatio > aspect.ratio) {
    const visible = aspect.ratio / aspect.listRatio;
    return { axis: 'vertical', band: (1 - visible) / 2 };
  }
  const visible = aspect.listRatio / aspect.ratio;
  return { axis: 'horizontal', band: (1 - visible) / 2 };
};

// Fixed-viewport "cover" cropper: the crop window is locked to the chosen aspect (poster/thumbnail),
// the underlying image is panned/zoomed behind it - whatever is visible in the window is the crop.
const ImageCropEditor = ({ image, initialAspect, initialTarget, seasonTargets, submitting, error, onCancel, onCreate }) => {
  const [aspectKey, setAspectKey] = useState(initialAspect === 'horizontal' ? 'horizontal' : 'vertical');
  const [target, setTarget] = useState(initialTarget ? String(initialTarget) : 'self');
  const [natural, setNatural] = useState(null); // { width, height }
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [allowOutOfFrame, setAllowOutOfFrame] = useState(false);
  const dragRef = useRef(null);

  const aspect = ASPECTS.find((a) => a.key === aspectKey);
  const { width: vw, height: vh } = aspect.viewport;
  const listMask = useMemo(() => getListMask(aspect), [aspect]);
  const minZoom = allowOutOfFrame ? MIN_ZOOM_FREE : MIN_ZOOM_LOCKED;

  const coverScale = useMemo(() => {
    if (!natural) return 1;
    return Math.max(vw / natural.width, vh / natural.height);
  }, [natural, vw, vh]);

  const scale = coverScale * zoom;
  const displayedW = natural ? natural.width * scale : 0;
  const displayedH = natural ? natural.height * scale : 0;

  const clampPos = (x, y, dw = displayedW, dh = displayedH) => {
    const slackX = allowOutOfFrame ? vw * OUT_OF_FRAME_SLACK : 0;
    const slackY = allowOutOfFrame ? vh * OUT_OF_FRAME_SLACK : 0;
    const minX = Math.min(0, vw - dw) - slackX;
    const maxX = Math.max(0, vw - dw) + slackX;
    const minY = Math.min(0, vh - dh) - slackY;
    const maxY = Math.max(0, vh - dh) + slackY;
    return { x: Math.max(minX, Math.min(maxX, x)), y: Math.max(minY, Math.min(maxY, y)) };
  };

  // Recenter whenever the aspect (viewport size) or the loaded image changes.
  useEffect(() => {
    if (!natural) return;
    const s = Math.max(vw / natural.width, vh / natural.height);
    const dw = natural.width * s;
    const dh = natural.height * s;
    setZoom(1);
    setPos({ x: (vw - dw) / 2, y: (vh - dh) / 2 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aspectKey, natural]);

  // Toggling "out of frame" changes both the allowed zoom and pan range - re-clamp so the image
  // doesn't end up stuck past the new bounds (and snap zoom back up if it was under the locked min).
  useEffect(() => {
    if (!natural) return;
    if (!allowOutOfFrame && zoom < MIN_ZOOM_LOCKED) {
      const nextScale = coverScale * MIN_ZOOM_LOCKED;
      const dw = natural.width * nextScale;
      const dh = natural.height * nextScale;
      setZoom(MIN_ZOOM_LOCKED);
      setPos((prev) => clampPos(prev.x, prev.y, dw, dh));
    } else {
      setPos((prev) => clampPos(prev.x, prev.y));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowOutOfFrame]);

  const handleImgLoad = (e) => {
    setNatural({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  };

  const handleZoomChange = (e) => {
    const nextZoom = Math.max(minZoom, Math.min(MAX_ZOOM, parseFloat(e.target.value)));
    const nextScale = coverScale * nextZoom;
    const dw = natural.width * nextScale;
    const dh = natural.height * nextScale;
    setZoom(nextZoom);
    setPos((prev) => clampPos(prev.x, prev.y, dw, dh));
  };

  const handlePointerDown = (e) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origin: pos };
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos(clampPos(dragRef.current.origin.x + dx, dragRef.current.origin.y + dy));
  };

  const handlePointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* no-op */ }
  };

  const handleCreate = () => {
    if (!natural) return;
    const crop = {
      x: -pos.x / scale,
      y: -pos.y / scale,
      width: vw / scale,
      height: vh / scale,
    };
    onCreate({
      imageType: aspectKey,
      crop,
      seasonId: target === 'self' ? null : Number(target),
    });
  };

  return (
    <div className="crop-editor">
      <div className="crop-editor-header">
        <button type="button" className="crop-editor-back" onClick={onCancel} aria-label="Back">&larr;</button>
        <div className="crop-editor-aspect-tabs">
          {ASPECTS.map((a) => (
            <button
              key={a.key}
              type="button"
              className={`crop-editor-aspect-tab ${aspectKey === a.key ? 'active' : ''}`}
              onClick={() => setAspectKey(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      <div
        className="crop-editor-viewport"
        style={{ width: vw, height: vh }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {natural && (
          <div
            className="crop-editor-viewport-bg"
            style={{ backgroundImage: `url(${image.url})`, filter: `blur(${OUT_OF_FRAME_BLUR}px)` }}
          />
        )}
        <img
          src={image.url}
          alt={image.label}
          onLoad={handleImgLoad}
          draggable={false}
          style={{
            width: displayedW || 'auto',
            height: displayedH || 'auto',
            transform: `translate(${pos.x}px, ${pos.y}px)`,
          }}
        />
        {listMask?.axis === 'vertical' && (
          <>
            <div className="crop-editor-mask-top" style={{ height: `${listMask.band * 100}%` }} />
            <div className="crop-editor-mask-bottom" style={{ height: `${listMask.band * 100}%` }} />
          </>
        )}
        {listMask?.axis === 'horizontal' && (
          <>
            <div className="crop-editor-mask-left" style={{ width: `${listMask.band * 100}%` }} />
            <div className="crop-editor-mask-right" style={{ width: `${listMask.band * 100}%` }} />
          </>
        )}
      </div>

      {listMask && (
        <div className="crop-editor-hint">Dimmed edges are usually cropped out in media lists.</div>
      )}

      <div className="crop-editor-zoom">
        <span>Zoom</span>
        <input
          type="range"
          min={minZoom}
          max={MAX_ZOOM}
          step={0.01}
          value={zoom}
          onChange={handleZoomChange}
          disabled={!natural}
        />
      </div>

      <label className="crop-editor-outframe">
        <input
          type="checkbox"
          checked={allowOutOfFrame}
          onChange={(e) => setAllowOutOfFrame(e.target.checked)}
        />
        Allow moving image out of frame (zoom out to {MIN_ZOOM_FREE}x)
      </label>

      {seasonTargets && seasonTargets.length > 0 && (
        <div className="crop-editor-target">
          <label htmlFor="crop-editor-target-select">Apply to</label>
          <select id="crop-editor-target-select" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="self">This title</option>
            {seasonTargets.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
      )}

      {error && <div className="image-picker-error">{error}</div>}

      <div className="crop-editor-actions">
        <button type="button" className="btn btn-cancel" onClick={onCancel} disabled={submitting}>Cancel</button>
        <button type="button" className="btn btn-confirm" onClick={handleCreate} disabled={!natural || submitting}>
          {submitting ? 'Creating...' : 'Create'}
        </button>
      </div>
    </div>
  );
};

export default ImageCropEditor;
